class DrawerMenu extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.shell = this.querySelector('.cl-menu__shell');
    this.viewport = this.querySelector('.cl-menu__viewport');
    this.scrollBody = this.querySelector('.cl-menu__body');
    if (!this.shell || !this.viewport) return;
    this.panels = new Map(Array.from(this.querySelectorAll('[data-menu-panel]'), panel => [panel.id, panel]));
    this.tabs = Array.from(this.querySelectorAll('[data-menu-tab]'));
    this.panelOpeners = new Map();
    this.currentPanel = this.querySelector('[data-menu-initial]')?.id || 'cl-menu-home';
    this.isOpen = false;
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    this.showPanel(this.currentPanel, false);
    const options = { capture: true, signal: this.controller.signal };
    document.addEventListener('click', event => this.onDocumentClick(event), options);
    document.addEventListener('keydown', event => this.onDocumentKeydown(event), options);
    document.addEventListener('focusin', event => this.onDocumentFocus(event), options);
    this.observer = new MutationObserver(() => this.syncOpenState());
    this.observer.observe(this, { attributes: true, attributeFilter: ['class'] });
    this.syncOpenState();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    if (this.isOpen) this.releaseFocus(false);
  }

  onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    if (this.contains(target)) {
      const close = target.closest('[data-close], [data-overlay]');
      if (close) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close();
        return;
      }
      const button = target.closest('[data-menu-target], [data-menu-tab], [data-menu-back]');
      if (!button) return;
      event.preventDefault();
      const panelId = button.dataset.menuTarget || button.dataset.menuTab || button.dataset.menuBack;
      if (!this.panels.has(panelId)) return;
      if (button.dataset.menuBack) {
        const opener = this.panelOpeners.get(this.currentPanel);
        this.showPanel(panelId, false);
        if (opener && this.isVisible(opener)) opener.focus();
        else this.focusHeading();
      } else {
        this.panelOpeners.set(panelId, button);
        this.showPanel(panelId);
      }
      return;
    }
    const specificOpener = target.closest('[data-drawer-submenu-open]');
    const menuOpener = target.closest('a[href="#drawer-menu"]');
    if (!specificOpener && !menuOpener) return;
    const opener = specificOpener || menuOpener;
    let panelId = 'cl-menu-home';
    if (specificOpener) {
      const requestedUrl = specificOpener.dataset.drawerSubmenuOpen;
      const requestedTitle = this.normalize(specificOpener.textContent);
      const candidates = Array.from(this.querySelectorAll('[data-menu-target][data-menu-url]'));
      const match = candidates.find(button => this.normalize(button.dataset.menuTitle) === requestedTitle)
        || candidates.find(button => button.dataset.menuUrl === requestedUrl);
      if (!match) return;
      panelId = match.dataset.menuTarget;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open(panelId, opener);
  }

  normalize(value = '') {
    return value.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  }

  open(panelId, opener) {
    this.returnFocus = opener || document.activeElement;
    this.showPanel(panelId, false);
    this.classList.add('initialized', 'is-open');
    document.body.classList.add('overflow-hidden');
    this.syncOpenState();
  }

  close() {
    const modals = window.Woolman?.ModalsAndDrawers;
    if (modals) modals.closeModalOrDrawer(this.id);
    else this.classList.remove('is-open');
    this.syncOpenState();
  }

  syncOpenState() {
    const open = this.classList.contains('is-open');
    if (open === this.isOpen) return;
    this.isOpen = open;
    this.setAttribute('aria-hidden', String(!open));
    this.inert = !open;
    if (open) {
      if (!this.returnFocus || this.contains(this.returnFocus)) this.returnFocus = document.activeElement;
      requestAnimationFrame(() => {
        if (this.isOpen) this.focusHeading();
      });
    } else this.releaseFocus(true);
  }

  releaseFocus(restore) {
    const otherModal = document.querySelector('.drawer.is-open:not(#drawer-menu), .modal.is-open');
    if (!otherModal && typeof removeTrapFocus === 'function') removeTrapFocus();
    document.body.classList.toggle('overflow-hidden', Boolean(otherModal));
    if (restore && !otherModal) {
      const originalOpener = this.returnFocus;
      const focusTarget = originalOpener?.isConnected && this.isVisible(originalOpener)
        ? originalOpener
        : Array.from(document.querySelectorAll('a[href="#drawer-menu"], #MainHeader [data-drawer-submenu-open]'))
          .find(opener => !this.contains(opener) && this.isVisible(opener));
      focusTarget?.focus({ preventScroll: true });
    }
    this.returnFocus = null;
  }

  showPanel(panelId, moveFocus = true) {
    const panel = this.panels.get(panelId);
    if (!panel) return;
    const rootId = panel.dataset.rootPanel || panelId;
    this.panels.forEach(item => {
      const selected = item === panel;
      item.hidden = !selected;
      item.inert = !selected;
    });
    this.querySelectorAll('[data-menu-target]').forEach(button => {
      button.setAttribute('aria-expanded', String(button.dataset.menuTarget === panelId));
    });
    this.tabs.forEach(tab => {
      const selected = tab.dataset.menuTab === rootId;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    this.querySelectorAll('[data-menu-rail]').forEach(rail => {
      rail.hidden = rail.dataset.menuRail !== rootId;
      rail.inert = rail.hidden;
    });
    this.currentPanel = panelId;
    this.viewport.scrollTop = 0;
    if (this.scrollBody) this.scrollBody.scrollTop = 0;
    if (moveFocus && this.isOpen) this.focusHeading();
  }

  focusHeading() {
    const heading = this.panels.get(this.currentPanel)?.querySelector('[data-menu-heading]');
    (heading || this.shell).focus({ preventScroll: true });
  }

  isVisible(element) {
    return !element.closest('[hidden], [inert]') && element.getClientRects().length > 0
      && getComputedStyle(element).visibility !== 'hidden';
  }

  focusableElements() {
    return Array.from(this.shell.querySelectorAll('a[href], button:not(:disabled), input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'))
      .filter(element => element.tabIndex >= 0 && this.isVisible(element));
  }

  onDocumentKeydown(event) {
    if (!this.isOpen) return;
    if (document.querySelector('.drawer.is-open:not(#drawer-menu), .modal.is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.close();
      return;
    }
    const tab = event.target.closest?.('[data-menu-tab]');
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const index = this.tabs.indexOf(tab);
      let next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
      this.showPanel(this.tabs[next].dataset.menuTab, false);
      this.tabs[next].focus();
      return;
    }
    if (event.key !== 'Tab') return;
    event.stopImmediatePropagation();
    const focusable = this.focusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.shell.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const index = focusable.indexOf(active);
    if (event.shiftKey && (index <= 0 || !this.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !this.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  onDocumentFocus(event) {
    if (!this.isOpen) return;
    if (document.querySelector('.drawer.is-open:not(#drawer-menu), .modal.is-open')) return;
    if (!this.shell.contains(event.target) || !this.isVisible(event.target)) this.focusHeading();
  }
}

if (!customElements.get('drawer-menu')) customElements.define('drawer-menu', DrawerMenu);
